"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.CallLobby = void 0;
const jsx_runtime_1 = require("react/jsx-runtime");
const react_1 = require("react");
const detect_browser_1 = require("detect-browser");
const ui_react_1 = require("@streem/ui-react");
const device_troubleshooting_modal_1 = require("../device_troubleshooting/device_troubleshooting_modal");
const controls_1 = require("./controls");
const intro_1 = require("./intro");
const invitation_details_1 = require("../../context/invitation_details");
const company_settings_1 = require("../../context/company_settings");
const video_frame_preview_1 = require("../in_call/video_frame/video_frame_preview");
const __1 = require("../..");
const room_session_1 = require("../../context/room_session");
const analytics_1 = require("@streem/analytics");
const join_now_button_1 = require("./join_now_button");
const test_audio_modal_1 = require("../test_audio_modal");
function CallLobby({ startCall, initialized }) {
    const ANIMATION_DURATION = 350;
    const previewContainerRef = (0, react_1.useRef)(null);
    const cameraOffRef = (0, react_1.useRef)(null);
    const theme = (0, ui_react_1.useTheme)();
    const hostRoomSession = (0, room_session_1.useRoomSession)();
    const { agentAvatar, agentName, agentBio, companyName } = (0, invitation_details_1.useInvitationDetails)();
    const { logoUrl } = (0, company_settings_1.useCompanySettings)();
    const [localVideoController] = (0, __1.useObservable)(hostRoomSession.media.localVideoController);
    const [audioState] = (0, __1.useObservable)(hostRoomSession.media.localAudio.enabled);
    const [audioConnected] = (0, __1.useObservable)(hostRoomSession.media.localAudio.connected);
    const [videoState] = (0, __1.useVideoEnabled)(hostRoomSession.media.localVideoController);
    const [userMediaVideoError] = (0, __1.useObservable)(hostRoomSession.media.getUserMediaVideoError);
    const [userMediaAudioError] = (0, __1.useObservable)(hostRoomSession.media.getUserMediaAudioError);
    const [showAudioModal, setShowAudioModal] = (0, react_1.useState)(false);
    const [animating, setAnimating] = (0, react_1.useState)(false);
    const [deviceTroubleshootingModalConfig, setDeviceTroubleshootingModalConfig] = (0, react_1.useState)({ deviceType: 'Microphone', showDeviceTroubleshootingModal: false });
    const cameraPermissionsDenied = userMediaVideoError === 'NotAllowedError';
    const microphonePermissionsDenied = userMediaAudioError === 'NotAllowedError';
    (0, react_1.useEffect)(() => {
        (0, analytics_1.recordScreenViewed)('call: lobby');
    }, []);
    (0, react_1.useEffect)(() => {
        if (microphonePermissionsDenied) {
            setDeviceTroubleshootingModalConfig({
                deviceType: 'Microphone',
                showDeviceTroubleshootingModal: true,
            });
        }
        else if (cameraPermissionsDenied) {
            setDeviceTroubleshootingModalConfig({
                deviceType: 'Camera',
                showDeviceTroubleshootingModal: true,
            });
        }
    }, [microphonePermissionsDenied, cameraPermissionsDenied]);
    /*  NOTE: At the moment this only seems to work with Chrome/Android browsers.
        I'm honestly not sure why, since we're doing all the things that Safari wants us to do.
        But, even node packages like `fscreen` throw errors when trying to use the vendor-prefix polyfill
        Safari decides when to go fullscreen on it's own pretty well though, and the address bar is less obtrusive than Chrome.
    */
    const setFullScreenModeAndStartCall = () => {
        const docEl = document.documentElement;
        const isDev = process.env.NODE_ENV === 'development';
        const browser = (0, detect_browser_1.detect)();
        if (!isDev && (browser === null || browser === void 0 ? void 0 : browser.os) !== 'iOS') {
            try {
                docEl.requestFullscreen();
            }
            catch (error) {
                console.log("Couldn't go to fullscreen mode. Probably you're in webkit: ", error);
            }
        }
        // regardless, start the call.
        startCall();
    };
    const handleJoinNowClick = () => {
        if (previewContainerRef && previewContainerRef.current && !animating) {
            setAnimating(true);
            const { top, width, height } = previewContainerRef.current.getBoundingClientRect();
            const animationKeyframes = [
                {
                    top: '0px',
                    width: `${width}px`,
                    height: `${height}px`,
                },
                {
                    top: `-${top}px`,
                    width: `${window.innerWidth}px`,
                    height: `${window.innerHeight}px`,
                    borderRadius: '0px',
                },
            ];
            const animation = previewContainerRef.current.animate(animationKeyframes, {
                duration: ANIMATION_DURATION,
                fill: 'forwards',
            });
            animation.onfinish = () => {
                setAnimating(false);
                setFullScreenModeAndStartCall();
            };
        }
        else if (cameraOffRef && cameraOffRef.current && !animating) {
            setAnimating(true);
            const { top, width, height } = cameraOffRef.current.getBoundingClientRect();
            const animationKeyframes = [
                {
                    top: '0px',
                    width: `${width}px`,
                    height: `${height}px`,
                },
                {
                    top: `-${top}px`,
                    width: `${window.innerWidth}px`,
                    height: `${window.innerHeight}px`,
                    borderRadius: '0px',
                },
            ];
            const animation = cameraOffRef.current.animate(animationKeyframes, {
                duration: ANIMATION_DURATION,
                fill: 'forwards',
            });
            animation.onfinish = () => {
                setAnimating(false);
                setFullScreenModeAndStartCall();
            };
        }
        else {
            setFullScreenModeAndStartCall();
        }
    };
    return ((0, jsx_runtime_1.jsxs)(jsx_runtime_1.Fragment, { children: [deviceTroubleshootingModalConfig.showDeviceTroubleshootingModal && ((0, jsx_runtime_1.jsx)(device_troubleshooting_modal_1.DeviceTroubleshootingModal, { permissionDenied: microphonePermissionsDenied || cameraPermissionsDenied, handleContinue: () => setDeviceTroubleshootingModalConfig({
                    deviceType: 'Microphone',
                    showDeviceTroubleshootingModal: false,
                }), inCall: false, device: deviceTroubleshootingModalConfig.deviceType })), (0, jsx_runtime_1.jsxs)(CenteredColumn, Object.assign({ width: "100vw", top: 0, bottom: 0, position: "fixed", overflow: "overlay", backgroundColor: "inherit" }, { children: [showAudioModal && ((0, jsx_runtime_1.jsx)(test_audio_modal_1.TestAudioModal, { "data-testid": "test-audio-modal", onClose: () => setShowAudioModal(false) })), (0, jsx_runtime_1.jsx)(intro_1.Intro, { avatarSrc: agentAvatar, displayName: agentName, bio: agentBio, company: companyName, logo: logoUrl, size: "small" }), (0, jsx_runtime_1.jsx)(ui_react_1.Box, Object.assign({ mt: '1.5vh', mb: '2vh', width: "60%" }, { children: (0, jsx_runtime_1.jsxs)(jsx_runtime_1.Fragment, { children: [(0, jsx_runtime_1.jsxs)(VideoContainer, Object.assign({ "data-testid": "video-container" }, { children: [videoState && localVideoController && ((0, jsx_runtime_1.jsx)(video_frame_preview_1.VideoFramePreview, { videoController: localVideoController, ref: previewContainerRef, animating: animating })), !videoState && ((0, jsx_runtime_1.jsxs)(CameraOffContainer, Object.assign({ ref: cameraOffRef }, { children: [(0, jsx_runtime_1.jsx)(ui_react_1.Box, Object.assign({ mb: 4 }, { children: (0, jsx_runtime_1.jsx)(ui_react_1.AppIcon, { name: "Face", color: theme.colors.grey30, size: 'large' }) })), (0, jsx_runtime_1.jsx)(ui_react_1.AppText, Object.assign({ "data-testid": "camera-off-message", color: "grey30" }, { children: "Your camera is off" }))] })))] })), (0, jsx_runtime_1.jsx)(controls_1.Controls, { audioState: {
                                        enabled: initialized ? !!audioState : false,
                                        notAvailable: !audioConnected || hostRoomSession.isUsingTwilioAndIOS18,
                                        toggleEnabled: () => {
                                            hostRoomSession.media.localAudio.setEnabled(!audioState);
                                        },
                                        showMicTroubleshootingMessage: () => {
                                            setDeviceTroubleshootingModalConfig({
                                                deviceType: 'Microphone',
                                                showDeviceTroubleshootingModal: true,
                                            });
                                        },
                                    }, videoState: {
                                        enabled: initialized ? !!videoState : false,
                                        notAvailable: cameraPermissionsDenied,
                                        toggleEnabled: () => {
                                            if (localVideoController) {
                                                localVideoController.setEnabled(!videoState);
                                            }
                                        },
                                        showCameraTroubleshootingMessage: () => {
                                            setDeviceTroubleshootingModalConfig({
                                                deviceType: 'Camera',
                                                showDeviceTroubleshootingModal: true,
                                            });
                                        },
                                    } })] }) })), (0, jsx_runtime_1.jsx)(join_now_button_1.JoinNowButton, { onClick: handleJoinNowClick, disabled: !initialized || cameraPermissionsDenied }), cameraPermissionsDenied && ((0, jsx_runtime_1.jsx)(ui_react_1.AppText, Object.assign({ color: "red50", bold: true, style: { marginTop: '5px' } }, { children: "Camera access is required to join the call." }))), (0, jsx_runtime_1.jsx)(ui_react_1.Box, Object.assign({ mt: "12px" }, { children: (0, jsx_runtime_1.jsx)(ui_react_1.Button, Object.assign({ "data-testid": "test-audio-button", variant: "inline", disabled: !initialized, fontColor: initialized ? 'inherit' : 'grey20', onClick: () => setShowAudioModal(true) }, { children: "Test Audio" })) }))] }))] }));
}
exports.CallLobby = CallLobby;
const VideoContainer = (0, ui_react_1.styled)(ui_react_1.Column)(({ theme }) => ({
    position: 'relative',
    zIndex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    color: theme.colors.grey40,
    backgroundColor: theme.colors.black,
    width: '100%',
    height: '40vh',
    borderRadius: '20px',
}));
const CameraOffContainer = (0, ui_react_1.styled)(ui_react_1.Column)(({ theme }) => ({
    position: 'absolute',
    zIndex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: theme.colors.grey90,
    width: '100%',
    height: '100%',
    borderRadius: '20px',
}));
const CenteredColumn = (0, ui_react_1.styled)(ui_react_1.Column)({
    alignItems: 'center',
    justifyContent: 'center',
});
//# sourceMappingURL=call_lobby.js.map